
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiBell, FiSearch } from 'react-icons/fi';
import ProfileMenu from '../ProfileMenu';
import { useAuth } from '../../context/AuthContext';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '../../services/supportService';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

const TopNavBar = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { user, profile } = useAuth();

  const routeTitles = [
    { path: '/app/dashboard', label: 'Dashboard' },
    { path: '/app/fresh-goods', label: 'Fresh Goods' },
    { path: '/app/market-research', label: 'Market Research' },
    { path: '/app/sandbox', label: 'Sandbox' },
    { path: '/app/industries', label: 'Industries (Data)' },
    { path: '/app/deals', label: 'Deals' },
    { path: '/app/individuals', label: 'Individuals' },
    { path: '/app/latest-news', label: 'News' },
    { path: '/app/conferences', label: 'Conferences' },
    { path: '/app/settings', label: 'Settings' },
    { path: '/app/support', label: 'Support' },
    { path: '/app/admin-tickets', label: 'Ticket Admin' },
    { path: '/app/automotive', label: 'Automotive' },
    { path: '/app/real-estate', label: 'Real Estate' },
    { path: '/app/energy', label: 'Energy' },
    { path: '/app/technology', label: 'Technology' },
  ];

  const pageTitle =
    routeTitles.find((route) => location.pathname.startsWith(route.path))
      ?.label || 'Data Analytics';

  const loadNotifications = useCallback(async () => {
    if (user?.id) {
      const data = await fetchNotifications(user.id);
      setNotifications(data);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkRead = async (id) => {
    await markNotificationRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const handleMarkAllRead = async () => {
    if (user?.id) {
      await markAllNotificationsRead(user.id);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }
  };

  const toggleProfile = () => {
    setProfileOpen((prev) => !prev);
    setNotifOpen(false);
  };

  const handleSettings = () => {
    setProfileOpen(false);
    navigate('/app/settings');
  };

  const handleLogout = () => {
    setProfileOpen(false);
    onLogout?.();
  };

  return (
    <header className="top-nav">
      <div className="top-nav-left" style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
        <img src={process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/logoMain.png' : '/logoMain.png'} alt="Trelledex Logo" className="brand-logo" style={{ width: 38, height: 38, objectFit: 'contain', borderRadius: 10 }} />
        <div>
          <h1 className="page-title" style={{ fontWeight: 700, fontSize: 22, margin: 0, lineHeight: 1.2 }}>{pageTitle}</h1>
          {(profile || user) && (() => {
            const displayedName = profile?.full_name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || '';
            return (
              <p style={{ fontSize: 11, color: '#5c6a82', margin: 0, lineHeight: 1 }}>
                {displayedName}{profile?.role && profile.role !== 'viewer' ? ` · ${ROLE_LABELS[profile.role] || profile.role}` : ''}{profile?.company ? ` · ${profile.company}` : ''}
              </p>
            );
          })()}
        </div>
      </div>
      <div className="top-nav-actions">
        <button className="icon-button" aria-label="Search">
          <FiSearch />
        </button>
        <div style={{ position: 'relative' }}>
          <button className="icon-button" aria-label="Notifications" onClick={() => { setNotifOpen(o => !o); setProfileOpen(false); }}>
            <FiBell />
            {unreadCount > 0 && <span className="notification-dot">{unreadCount}</span>}
          </button>
          {notifOpen && (
            <div className="profile-dropdown" style={{ right: 0, width: 340, maxHeight: 400, overflowY: 'auto', padding: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #d6deea' }}>
                <strong style={{ fontSize: 14 }}>Notifications</strong>
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} style={{ border: 'none', background: 'none', color: '#2d388a', fontSize: 12, cursor: 'pointer' }}>
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <p style={{ padding: 16, textAlign: 'center', color: '#5c6a82', fontSize: 13 }}>No notifications yet</p>
              ) : (
                notifications.slice(0, 20).map((n) => (
                  <div
                    key={n.id}
                    onClick={() => { handleMarkRead(n.id); if (n.link) { navigate(n.link); setNotifOpen(false); } }}
                    style={{
                      padding: '10px 16px',
                      borderBottom: '1px solid #eef2f8',
                      background: n.read ? '#fff' : '#e6f1fb',
                      cursor: n.link ? 'pointer' : 'default',
                    }}
                  >
                    <p style={{ fontSize: 13, fontWeight: n.read ? 400 : 600, color: '#0f1a33', margin: 0 }}>{n.title}</p>
                    {n.message && <p style={{ fontSize: 12, color: '#5c6a82', margin: '2px 0 0' }}>{n.message}</p>}
                    <p style={{ fontSize: 11, color: '#5c6a82', margin: '4px 0 0' }}>{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <ProfileMenu
          isOpen={profileOpen}
          onToggle={toggleProfile}
          onSettings={handleSettings}
          onLogout={handleLogout}
        />
      </div>
    </header>
  );
};

export default TopNavBar;
